<script setup lang="ts">
import { onBeforeUnmount, watch } from 'vue'
import { storeToRefs } from 'pinia'
import CopyTrackWrapper from '@/components/CopyTrackWrapper.vue'
import PresentLevelsConclusionTabs from '@/presentlevels/no-ai/PresentLevelsConclusionTabs.vue'
import { getPresentLevelStudentStatementDraft } from '@/presentlevels/api_v1.js'
import { usePresentLevelsWizardStore } from '@/presentlevels/no-ai/present_levels_wizard.store'
import type { AlignedAssessment } from '@/presentlevels/no-ai/present_levels_wizard.store'
import { useVisibilityStore } from '@/visibility/visibility.store'
import type { StatementsByArea } from '@/presentlevels/no-ai/goal_recommendations'

type DraftedStudent = {
  student_name: string
  drafted_strengths: string | null
  enrolled_grade_level: string
  selected_subjects: string[]
  updated_at: string
  resource_docs: AlignedAssessment[] | null
  statements_by_area: StatementsByArea
}

type DraftResponse = {
  student: DraftedStudent
}

type DraftParams = Record<string, string>

type RequestError = {
  status_code?: number
}

const FORBIDDEN = 403

const props = defineProps<{
  uuid: string
}>()

const wizardStore = usePresentLevelsWizardStore()
const { iodVisibility, effectsVisibility } = storeToRefs(useVisibilityStore())

function draftParams(): DraftParams {
  const areImpactsIncluded = iodVisibility.value || effectsVisibility.value
  const params: DraftParams = areImpactsIncluded ? { includeImpacts: 'true' } : { noIOD: 'true' }
  if (effectsVisibility.value) {
    params.effects = 'true'
  }
  return params
}

function isForbidden(error: unknown) {
  return (error as RequestError | null)?.status_code === FORBIDDEN
}

function showDraft(student: DraftedStudent) {
  wizardStore.setConclusion({
    statement: student.drafted_strengths ?? '',
    resourceDocs: student.resource_docs ?? []
  })
  wizardStore.lastUpdate = student.updated_at
  wizardStore.setBreadcrumbsInfo({ uuid: props.uuid, availableStages: student.selected_subjects })
  wizardStore.setStudentInfo({
    studentName: student.student_name,
    enrolledGrade: student.enrolled_grade_level,
    areasOfNeed: student.selected_subjects
  })
  wizardStore.setDraftedSkillsFromStatements(student.statements_by_area)
  wizardStore.retrieveRelevantGoals({ statementsByArea: student.statements_by_area, grade: student.enrolled_grade_level })
}

function clearPreviousDraft() {
  wizardStore.setConclusion({ statement: null, resourceDocs: [] })
  wizardStore.resetStudentInfo()
  wizardStore.resetGoalRecommendations()
  wizardStore.resetDraftedSkills()
  wizardStore.lastUpdate = null
  wizardStore.hasAccess = null
}

let latestLoad = 0

async function loadDraft() {
  const load = ++latestLoad
  const isStale = () => load !== latestLoad
  clearPreviousDraft()
  try {
    const draft = await getPresentLevelStudentStatementDraft(props.uuid, draftParams()) as DraftResponse
    if (isStale()) {
      return
    }
    wizardStore.setHasAccess(true)
    showDraft(draft.student)
  } catch (error) {
    if (isStale()) {
      return
    }
    if (isForbidden(error)) {
      wizardStore.setHasAccess(false)
      return
    }
    console.error(error)
  }
}

watch(() => props.uuid, loadDraft, { immediate: true })
onBeforeUnmount(() => { latestLoad++ })
</script>

<template>
  <CopyTrackWrapper type="presentlevel" :uuid="uuid" gate-copy>
    <PresentLevelsConclusionTabs>
      <router-view />
    </PresentLevelsConclusionTabs>
  </CopyTrackWrapper>
</template>
